import React, { useLayoutEffect, useRef, useState } from 'react';
import { AppState, Platform } from 'react-native';
import { hydrate, IsRestoringProvider, QueryClientProvider } from '@tanstack/react-query';
import { createBatchedPersistence } from '@/utils/react-query/persistence';

const QueryPersistenceProvider = ({ children, client, persistOptions, sessionKey = 0, onSuccess, onError }) => {
    const { persister, buster = '', maxAge = 24 * 60 * 60 * 1000 } = persistOptions;
    const latest = useRef({ persistOptions, onSuccess, onError });
    const [restored, setRestored] = useState(null);
    const isRestoring = !restored || restored.client !== client || restored.persister !== persister ||
        restored.buster !== buster || restored.maxAge !== maxAge || restored.sessionKey !== sessionKey;

    useLayoutEffect(() => {
        latest.current = { persistOptions, onSuccess, onError };
    }, [persistOptions, onSuccess, onError]);

    // Cancel the old identity's reader/writer during commit, before a queued
    // promise continuation can hydrate it into the next session.
    useLayoutEffect(() => {
        let live = true;
        let generation = persister.getGeneration();
        let persistence;
        let appState;
        const flush = () => { if (persistence) void persistence.flush(); };
        const visibility = () => { if (document.visibilityState === 'hidden') flush(); };
        const options = { ...latest.current.persistOptions, queryClient: client };
        const current = () => live && generation === persister.getGeneration();
        const removeSnapshot = async () => {
            const removed = persister.removeClient();
            generation = persister.getGeneration();
            await removed;
        };

        const restore = async () => {
            // TEMP DEBUG: repeating restores = hydrate() flooding the cache —
            // every query's data is replaced per pass → observer/render churn.
            if (__DEV__) console.log(`[qpp-restore] restoring @ ${Date.now()}`);
            try {
                const snapshot = await persister.restoreClient();
                // Keep TanStack's persisted envelope/expiry rules, but never
                // hydrate a snapshot after this identity/provider was replaced.
                if (!current()) return;
                if (snapshot) {
                    if (!snapshot.timestamp || Date.now() - snapshot.timestamp > maxAge || snapshot.buster !== buster) {
                        await removeSnapshot();
                    } else {
                        hydrate(client, snapshot.clientState, options.hydrateOptions);
                    }
                }
                if (current()) await latest.current.onSuccess?.();
            } catch (error) {
                if (!current()) return;
                await removeSnapshot().catch(() => {});
                if (current()) await latest.current.onError?.(error);
            } finally {
                if (live) {
                    if (current()) {
                        persistence = createBatchedPersistence(options, {
                            onError: error => console.warn('[query persistence] save failed:', error?.message),
                        });
                        appState = AppState.addEventListener('change', state => { if (state !== 'active') flush(); });
                        if (Platform.OS === 'web' && typeof window !== 'undefined') {
                            window.addEventListener('pagehide', flush);
                            document.addEventListener('visibilitychange', visibility);
                        }
                    }
                    setRestored({ client, persister, buster, maxAge, sessionKey });
                }
            }
        };
        void restore().catch(error => console.warn('[query persistence] restore failed:', error?.message));
        return () => {
            live = false;
            persistence?.stop();
            appState?.remove();
            if (Platform.OS === 'web' && typeof window !== 'undefined') {
                window.removeEventListener('pagehide', flush);
                document.removeEventListener('visibilitychange', visibility);
            }
        };
    }, [client, persister, buster, maxAge, sessionKey]);

    return <QueryClientProvider client={client}>
        <IsRestoringProvider value={isRestoring}>{children}</IsRestoringProvider>
    </QueryClientProvider>;
};

export default QueryPersistenceProvider;
